import React, { useState } from 'react';
import { Hexagon, User, Mail, Lock, LogIn, UserPlus, Ghost } from 'lucide-react';
import './AuthPage.css';

const API_URL = process.env.REACT_APP_API_URL || "http://localhost:8005";

function AuthPage({ onAuthSuccess }) {
  const [isLogin, setIsLogin] = useState(true);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  // Form states
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");

  const handleEmailChange = (e) => {
    const newEmail = e.target.value;
    setEmail(newEmail);
    // Auto-fill username if empty or if it matches the previous email base
    if (!isLogin) {
      const base = newEmail.split('@')[0];
      setUsername(base);
    }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);

    try {
      const res = await fetch(`${API_URL}/login`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ username, password })
      });

      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || "Login failed");

      onAuthSuccess(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    setError("");
    
    if (password.length < 8) {
      setError("Password must be at least 8 characters long.");
      return;
    }

    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/register`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ username, email, password, name })
      });

      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || "Registration failed");

      onAuthSuccess(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleGuestLogin = async () => {
    setError("");
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/guest`, {
        method: "POST",
      });

      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || "Guest login failed");

      onAuthSuccess(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="auth-container">
      <div className="auth-background">
        <div className="glow-orb orb-1"></div>
        <div className="glow-orb orb-2"></div>
        <div className="glow-orb orb-3"></div>
      </div>

      <div className="auth-card glass-panel">
        <div className="auth-header">
          <Hexagon className="auth-logo" size={48} />
          <h1>FOG OF LIFE MAP</h1>
          <p>Uncover your world, one hex at a time.</p>
        </div>

        <div className="auth-tabs">
          <button 
            className={`auth-tab ${isLogin ? 'active' : ''}`}
            onClick={() => { setIsLogin(true); setError(""); }}
          >
            Log In
          </button>
          <button 
            className={`auth-tab ${!isLogin ? 'active' : ''}`}
            onClick={() => { setIsLogin(false); setError(""); }}
          >
            Register
          </button>
        </div>

        {error && <div className="auth-error">{error}</div>}

        {isLogin ? (
          <form className="auth-form" onSubmit={handleLogin}>
            <div className="input-group">
              <User className="input-icon" size={20} />
              <input 
                type="text" 
                placeholder="User ID" 
                value={username} 
                onChange={(e) => setUsername(e.target.value)}
                required
              />
            </div>
            <div className="input-group">
              <Lock className="input-icon" size={20} />
              <input 
                type="password" 
                placeholder="Password" 
                value={password} 
                onChange={(e) => setPassword(e.target.value)}
                required
              />
            </div>
            <button type="submit" className="auth-btn primary-btn" disabled={loading}>
              {loading ? "Authenticating..." : <><LogIn size={20} /> Log In</>}
            </button>
          </form>
        ) : (
          <form className="auth-form" onSubmit={handleRegister}>
            <div className="input-group">
              <User className="input-icon" size={20} />
              <input 
                type="text" 
                placeholder="Full Name" 
                value={name} 
                onChange={(e) => setName(e.target.value)}
                required
              />
            </div>
            <div className="input-group">
              <Mail className="input-icon" size={20} />
              <input 
                type="email" 
                placeholder="Email Address" 
                value={email} 
                onChange={handleEmailChange}
                required
              />
            </div>
            <div className="input-group">
              <UserPlus className="input-icon" size={20} />
              <input 
                type="text" 
                placeholder="User ID" 
                value={username} 
                onChange={(e) => setUsername(e.target.value)}
                required
              />
            </div>
            <div className="input-group">
              <Lock className="input-icon" size={20} />
              <input 
                type="password" 
                placeholder="Password (Min 8 chars)" 
                value={password} 
                onChange={(e) => setPassword(e.target.value)}
                required
                minLength={8}
              />
            </div>
            <button type="submit" className="auth-btn primary-btn" disabled={loading}>
              {loading ? "Registering..." : <><UserPlus size={20} /> Create Account</>}
            </button>
          </form>
        )}

        <div className="auth-divider">
          <span>OR</span>
        </div>

        <button 
          className="auth-btn guest-btn" 
          onClick={handleGuestLogin}
          disabled={loading}
        >
          <Ghost size={20} /> Continue as Guest
        </button>
      </div>
    </div>
  );
}

export default AuthPage;
